import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import notification from "antd/es/notification";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import User from "@mapmycustomers/shared/types/User";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { ButtonProps } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import configService from "@app/config/ConfigService";
import Path from "@app/enum/Path";
import { getTrashFolder } from "@app/scene/resources/store";
import {
  deleteResource,
  pinUnpinResource,
  updateResource,
} from "@app/scene/resources/store/actions";
import { downloadFile } from "@app/store/files/actions";
import { getCurrentUserId } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { userDisplayName } from "@app/util/formatters";

import ShareModal from "../ShareModal";

import messages from "./messages";
import MoveToFolderModal from "./MoveToFolderModal";
import RenameModal from "./RenameModal";
import useItems, { analyticsNames, ResourceAction } from "./useItems";

export interface ActionButtonProps {
  currentUserId: User["id"];
  folderId?: string;
  isPinned?: boolean;
  onDeleteResource: typeof deleteResource.request;
  onDownloadFile: typeof downloadFile;
  onPinUnpinResource: typeof pinUnpinResource.request;
  onUpdateResource: typeof updateResource.request;
  resource: Resource;
  size?: ButtonProps["size"];
  trashFolder?: Folder;
  users: User[];
}

const ActionButton: FC<ActionButtonProps> = ({
  currentUserId,
  folderId,
  isPinned = false,
  onDeleteResource,
  onDownloadFile,
  onPinUnpinResource,
  onUpdateResource,
  resource,
  size = "large",
  trashFolder,
  users,
}) => {
  const analytics = useExtendedAnalytics("File Ellipsis Menu", useAnalytics());
  const intl = useIntl();
  const isTrash = isNotEmpty(folderId) && trashFolder?.id === parseInt(folderId);

  const [shareModal, showShareModal, hideShareModal] = useBoolean();
  const [renameModalOpen, showRenameModal, hideRenameModal] = useBoolean();
  const [moveToFolderModalOpen, showMoveToFolderModal, hideMoveToFolderModal] = useBoolean();

  const handleShare = useCallback(
    (userIds: User["id"][]) => {
      onUpdateResource({
        resource: {
          id: resource.id,
          userIds,
        },
        successCallback: () => {
          const sharedWith = userIds.filter((id) => currentUserId !== id);
          const user =
            sharedWith.length === 1 ? users.find(({ id }) => sharedWith.includes(id)) : undefined;

          notification.success({
            message: intl.formatMessage(messages.resourceShareSuccess, {
              count: sharedWith.length,
              resourceName: resource.name,
              userName: user ? userDisplayName(user) : undefined,
            }),
          });
          analytics.completed(["Share Resource"]);
          hideShareModal();
        },
      });
    },
    [
      onUpdateResource,
      resource.id,
      resource.name,
      users,
      intl,
      analytics,
      hideShareModal,
      currentUserId,
    ]
  );

  const handleDownload = useCallback(() => {
    if (resource.file) {
      onDownloadFile({ fileId: resource.file.id, fileName: resource.name });
    }
  }, [onDownloadFile, resource.file, resource.name]);

  const handleDelete = useCallback(() => {
    onDeleteResource({ id: resource.id, isTrash, name: resource.name });
  }, [onDeleteResource, resource.id, resource.name, isTrash]);

  const handleCopyLink = useCallback(
    async (info: MenuInfo) => {
      // To prevent resource item click event propagation
      stopEvents(info.domEvent);
      const urlToCopy = resource.url
        ? resource.url
        : `${configService.getBaseAppUrl()}${Path.RESOURCES}${
            folderId ? `/folders/${folderId}` : ""
          }/files/${resource.id}`;
      await navigator.clipboard.writeText(urlToCopy);
    },
    [folderId, resource.id, resource.url]
  );

  const handlePinUnpin = useCallback(() => {
    onPinUnpinResource({ ids: [resource.id], unpin: isPinned });
  }, [isPinned, onPinUnpinResource, resource.id]);

  const handleRemoveFromFolder = useCallback(() => {
    onUpdateResource({
      resource: {
        id: resource.id,
        folder: null,
      },
      successCallback: () => {
        notification.success({
          message: intl.formatMessage(messages.removeFromFolderSuccess),
        });
      },
    });
  }, [intl, onUpdateResource, resource.id]);

  const handleRestore = useCallback(() => {
    onUpdateResource({
      resource: {
        id: resource.id,
        folder: null,
      },
      successCallback: () => {
        notification.success({
          message: intl.formatMessage(messages.restoreSuccess),
        });
      },
    });
  }, [intl, onUpdateResource, resource.id]);

  const items = useItems(
    handleCopyLink,
    handleDelete,
    handleDownload,
    folderId,
    resource,
    isPinned,
    showMoveToFolderModal,
    showRenameModal,
    showShareModal,
    handlePinUnpin,
    handleRemoveFromFolder,
    handleRestore
  );

  const handleClick: MenuProps["onClick"] = useCallback(
    (info: MenuInfo) => {
      stopEvents(info.domEvent);
      analytics.clicked([analyticsNames[info.key as ResourceAction]]);
    },
    [analytics]
  );

  return (
    <>
      <Dropdown menu={{ items, onClick: handleClick }} trigger={["click"]}>
        <MoreButton onClick={stopEvents} size={size} />
      </Dropdown>
      <div onClick={stopEvents}>
        <ShareModal
          onChangeUserIds={handleShare}
          onHide={hideShareModal}
          open={shareModal}
          title={intl.formatMessage(messages.shareTitle, { name: resource.name })}
          userIds={resource.userIds}
        />
        <RenameModal onHide={hideRenameModal} open={renameModalOpen} resource={resource} />
        <MoveToFolderModal
          onHide={hideMoveToFolderModal}
          open={moveToFolderModalOpen}
          resource={resource}
        />
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  trashFolder: getTrashFolder(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onDeleteResource: deleteResource.request,
  onDownloadFile: downloadFile,
  onPinUnpinResource: pinUnpinResource.request,
  onUpdateResource: updateResource.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActionButton);
